import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash, MagnifyingGlass, User, Camera, UploadSimple, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'intermediário', label: 'Intermediario' },
  { value: 'avançado', label: 'Avancado' }
];

const turmaOptions = [
  { value: '', label: 'Sem turma' },
  { value: 'seg-1730-fem-av', label: 'Seg 17:30 - Feminino Avancado' },
  { value: 'seg-1830-masc-av-int', label: 'Seg 18:30 - Masc Avancado/Intermediario' },
  { value: 'ter-1830-ini', label: 'Ter 18:30 - Iniciante' },
  { value: 'ter-1830-fem-int', label: 'Ter 18:30 - Feminino Intermediario' },
  { value: 'ter-1830-masc-int', label: 'Ter 18:30 - Masculino Intermediario' },
  { value: 'qua-0700-av', label: 'Qua 07:00 - Avancado' },
  { value: 'qua-1730-fem-av', label: 'Qua 17:30 - Feminino Avancado' },
  { value: 'qua-1830-masc-av-int', label: 'Qua 18:30 - Masc Avancado/Intermediario' },
  { value: 'qui-1830-ini', label: 'Qui 18:30 - Iniciante' },
  { value: 'qui-1830-fem-int', label: 'Qui 18:30 - Feminino Intermediario' },
  { value: 'qui-1830-masc-int', label: 'Qui 18:30 - Masculino Intermediario' },
  { value: 'sex-0700-av', label: 'Sex 07:00 - Avancados' },
  { value: 'sex-1730-fem-ini-int', label: 'Sex 17:30 - Fem Iniciante/Intermediario' },
  { value: 'sex-1830-fem-av', label: 'Sex 18:30 - Feminino Avancado' },
  { value: 'sab-0830-todos', label: 'Sab 08:30 - Todos os Niveis' },
];

const emptyStudent = {
  name: '',
  email: '',
  phone: '',
  birth_date: '',
  category: 'iniciante',
  emergency_contact: '',
  notes: '',
  turmas: [],
  override_turmas: false
};

const PLAN_MAX_TURMAS = { '1x': 1, '2x': 2, '3x': 3 };

// Helper to load image as blob URL via authenticated request
async function loadImage(path) {
  try {
    const resp = await axios.get(`${API}/files/${path}`, {
      withCredentials: true,
      responseType: 'blob'
    });
    return URL.createObjectURL(resp.data);
  } catch {
    return null;
  }
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [realignLoading, setRealignLoading] = useState(false);

  useEffect(() => {
    if (auditOpen && !auditData) {
      setAuditLoading(true);
      axios.get(`${API}/admin/subscriptions/audit`, { withCredentials: true })
        .then((r) => setAuditData(r.data))
        .catch((e) => alert('Erro ao auditar: ' + (e.response?.data?.detail || e.message)))
        .finally(() => setAuditLoading(false));
    }
  }, [auditOpen, auditData]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [formData, setFormData] = useState(emptyStudent);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [studentPhotos, setStudentPhotos] = useState({});
  const fileInputRef = useRef(null);

  const fetchStudents = async () => {
    try {
      const { data } = await axios.get(`${API}/students`, { withCredentials: true });
      setStudents(data);
      // Load photos for students that have them
      const photos = {};
      for (const s of data) {
        if (s.photo_url) {
          const blobUrl = await loadImage(s.photo_url);
          if (blobUrl) photos[s.id] = blobUrl;
        }
      }
      setStudentPhotos(photos);
    } catch (e) {
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    return () => {
      // Cleanup blob URLs
      Object.values(studentPhotos).forEach(url => URL.revokeObjectURL(url));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenCreate = () => {
    setCurrentStudent(null);
    setFormData(emptyStudent);
    setPhotoPreview(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (student) => {
    setCurrentStudent(student);
    const existingTurmas = Array.isArray(student.turmas) && student.turmas.length > 0
      ? student.turmas
      : (student.turma ? [student.turma] : []);
    setFormData({
      name: student.name,
      email: student.email,
      phone: student.phone,
      birth_date: student.birth_date,
      category: student.category,
      emergency_contact: student.emergency_contact || '',
      notes: student.notes || '',
      turmas: existingTurmas,
      override_turmas: false,
      plan: student.plan || '1x'
    });
    setPhotoPreview(studentPhotos[student.id] || null);
    setDialogOpen(true);
  };

  const handleOpenDelete = (student) => {
    setCurrentStudent(student);
    setDeleteDialogOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const preview = URL.createObjectURL(file);
    setPhotoPreview(preview);

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Store the path for saving with the student
      setFormData(prev => ({ ...prev, _photo_path: data.path }));
      toast.success('Foto carregada com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer upload da foto');
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { _photo_path, turmas, override_turmas, plan, ...studentData } = formData;
      
      let studentId;
      if (currentStudent) {
        const updatePayload = { ...studentData };
        if (_photo_path) updatePayload.photo_url = _photo_path;
        await axios.put(`${API}/students/${currentStudent.id}`, updatePayload, { withCredentials: true });
        studentId = currentStudent.id;
        toast.success('Aluno atualizado com sucesso');
      } else {
        const res = await axios.post(`${API}/students`, studentData, { withCredentials: true });
        studentId = res.data.id;
        if (_photo_path && studentId) {
          await axios.put(`${API}/students/${studentId}`, { photo_url: _photo_path }, { withCredentials: true });
        }
        toast.success('Aluno cadastrado com sucesso');
      }

      // Sync turmas via the dedicated endpoint (respects plan limit / override)
      if (studentId) {
        try {
          await axios.post(`${API}/turmas/assign`, {
            student_id: studentId,
            turma_ids: turmas || [],
            override: !!override_turmas,
          }, { withCredentials: true });
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Aluno salvo mas erro ao atualizar turmas');
        }
      }

      setDialogOpen(false);
      fetchStudents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar aluno');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/students/${currentStudent.id}`, { withCredentials: true });
      toast.success('Aluno removido com sucesso');
      setDeleteDialogOpen(false);
      fetchStudents();
    } catch (e) {
      toast.error('Erro ao remover aluno');
    }
  };

  const [sortMode, setSortMode] = useState('name-asc'); // alunos default A-Z
  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (sortMode === 'name-desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
    if (sortMode === 'recent') return (b.created_at || '').localeCompare(a.created_at || '');
    return (a.name || '').localeCompare(b.name || '', 'pt-BR');
  });

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="students-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Alunos</h1>
            <p className="text-[#73706A] mt-1">Gerencie as matrículas da escola</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setAuditOpen(true)}
              variant="outline"
              className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
              data-testid="audit-subs-btn"
              title="Ver e alinhar vencimento das assinaturas"
            >
              Auditar assinaturas
            </Button>
            <Button
              onClick={handleOpenCreate}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="add-student-btn"
            >
              <Plus size={20} className="mr-2" />
              Novo Aluno
            </Button>
          </div>
        </div>

        {/* Search + Sort */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-students-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="students-sort-controls">
            <button
              onClick={() => setSortMode('name-asc')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="students-sort-asc"
            >
              A-Z
            </button>
            <button
              onClick={() => setSortMode('name-desc')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="students-sort-desc"
            >
              Z-A
            </button>
            <button
              onClick={() => setSortMode('recent')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'recent' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A] hover:text-[#1A1A1A]'}`}
              data-testid="students-sort-recent"
            >
              Recentes
            </button>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]">
              {search ? 'Nenhum aluno encontrado' : 'Nenhum aluno cadastrado'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Aluno</th>
                    <th className="table-header">Contato</th>
                    <th className="table-header">Turma</th>
                    <th className="table-header">Categoria</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((student, index) => (
                    <tr key={student.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`student-row-${index}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          {studentPhotos[student.id] ? (
                            <img
                              src={studentPhotos[student.id]}
                              alt={student.name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-[#E6E2D8]"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-[#0077B6] rounded-full flex items-center justify-center">
                              <User size={18} className="text-white" />
                            </div>
                          )}
                          <div>
                            <Link
                              to={`/students/${student.id}`}
                              className="font-medium text-[#1A1A1A] hover:text-[#E85D04] hover:underline transition-colors"
                              data-testid={`student-name-link-${index}`}
                              title="Ver pagamentos e historico"
                            >
                              {student.name}
                            </Link>
                            <p className="text-sm text-[#73706A]">{student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <p className="text-[#1A1A1A]">{student.phone}</p>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-col gap-0.5 max-w-[220px]">
                          {(() => {
                            const list = Array.isArray(student.turmas) && student.turmas.length > 0
                              ? student.turmas
                              : (student.turma ? [student.turma] : []);
                            if (list.length === 0) return <span className="text-xs text-[#73706A]">-</span>;
                            return list.map(tid => {
                              const t = turmaOptions.find(o => o.value === tid);
                              return (
                                <span key={tid} className="text-xs text-[#1A1A1A] truncate">
                                  {t?.label || tid}
                                </span>
                              );
                            });
                          })()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="badge badge-info capitalize">{student.category}</span>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${student.status === 'ativo' ? 'badge-success' : 'badge-warning'}`}>
                          {student.status}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/students/${student.id}`} data-testid={`view-payments-${index}`} title="Ver pagamentos e historico">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#2D6A4F] hover:text-[#1B4332] hover:bg-green-50"
                            >
                              <Eye size={18} />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(student)}
                            className="text-[#0077B6] hover:text-[#03045E] hover:bg-[#F5F2EB]"
                            data-testid={`edit-student-${index}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDelete(student)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-student-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {currentStudent ? 'Editar Aluno' : 'Novo Aluno'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div className="flex justify-center">
              <div className="relative">
                <div
                  className="w-24 h-24 rounded-full border-2 border-dashed border-[#E6E2D8] flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#E85D04] transition-colors bg-[#F5F2EB]"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-upload-area"
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <Camera size={24} className="text-[#73706A]" />
                      <span className="text-[10px] text-[#73706A]">Foto</span>
                    </div>
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-full">
                      <div className="w-6 h-6 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="photo-file-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="student-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="student-email-input"
                />
              </div>
              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="student-phone-input"
                />
              </div>
              <div>
                <Label>Data de nascimento *</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  required
                  data-testid="student-birthdate-input"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="student-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Contato de emergencia</Label>
                <Input
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  data-testid="student-emergency-input"
                />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Turmas (fixas)</Label>
                  {currentStudent && (() => {
                    const plan = currentStudent.plan || '1x';
                    const limit = PLAN_MAX_TURMAS[plan] || 1;
                    const overLimit = (formData.turmas?.length || 0) > limit;
                    return (
                      <span className={`text-xs ${overLimit && !formData.override_turmas ? 'text-[#9B2226]' : 'text-[#73706A]'}`}>
                        Plano {plan}: max {limit} turma(s) — selecionadas {formData.turmas.length}
                      </span>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto border border-[#E6E2D8] rounded-lg p-2 bg-white">
                  {turmaOptions.filter(t => t.value).map(t => {
                    const checked = formData.turmas?.includes(t.value);
                    return (
                      <label
                        key={t.value}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${checked ? 'bg-orange-50' : 'hover:bg-[#F5F2EB]'}`}
                        data-testid={`student-turma-${t.value}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              turmas: e.target.checked
                                ? [...(prev.turmas || []), t.value]
                                : (prev.turmas || []).filter(x => x !== t.value)
                            }));
                          }}
                          className="w-4 h-4 accent-[#E85D04]"
                        />
                        <span className="text-xs text-[#1A1A1A]">{t.label}</span>
                      </label>
                    );
                  })}
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs text-[#73706A] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.override_turmas}
                    onChange={(e) => setFormData({ ...formData, override_turmas: e.target.checked })}
                    className="w-3.5 h-3.5 accent-[#E85D04]"
                    data-testid="student-turma-override"
                  />
                  <span>Ignorar limite do plano (admin pode atribuir mais turmas que o plano permite)</span>
                </label>
              </div>
              <div className="col-span-2">
                <Label>Observações</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field min-h-[80px] resize-none"
                  data-testid="student-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || uploading}
                className="bg-[#E85D04] hover:bg-[#DC2F02]"
                data-testid="save-student-btn"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">
            Tem certeza que deseja remover o aluno <strong>{currentStudent?.name}</strong>?
            Esta ação não pode ser desfeita.
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-[#9B2226] hover:bg-[#7d1c1f]"
              data-testid="confirm-delete-student-btn"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit subscriptions modal */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Auditoria de Assinaturas (Stripe LIVE)</DialogTitle>
          </DialogHeader>
          {auditLoading ? (
            <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : !auditData ? (
            <p className="text-sm text-[#73706A]">Sem dados. Feche e abra novamente.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="stat-card"><p className="text-xs text-[#73706A]">Total ativas</p><p className="text-xl font-semibold font-['Outfit']">{auditData.total}</p></div>
                <div className="stat-card"><p className="text-xs text-[#73706A]">Ja dia 01</p><p className="text-xl font-semibold font-['Outfit'] text-[#2D6A4F]">{auditData.aligned_day_01}</p></div>
                <div className="stat-card"><p className="text-xs text-[#73706A]">Desalinhadas</p><p className="text-xl font-semibold font-['Outfit'] text-[#E85D04]">{auditData.misaligned}</p></div>
              </div>
              <div className="max-h-[320px] overflow-y-auto border border-[#E6E2D8] rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-[#F5F2EB] sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">Cliente</th>
                      <th className="px-2 py-2 text-left">Plano</th>
                      <th className="px-2 py-2 text-center">Dia atual</th>
                      <th className="px-2 py-2 text-left">Proximo vencimento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData.subscriptions || []).map((s) => (
                      <tr key={s.sub_id} className={`border-t border-[#E6E2D8] ${s.aligned_day_01 ? 'bg-green-50/40' : 'bg-red-50/40'}`}>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-[#1A1A1A]">{s.customer_name || '-'}</div>
                          <div className="text-[#73706A] text-[10px]">{s.customer_email}</div>
                        </td>
                        <td className="px-2 py-1.5 text-[#73706A]">{s.plan_id || '-'} (${s.amount.toFixed(2)})</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`badge ${s.aligned_day_01 ? 'badge-success' : 'badge-warning'}`}>{s.current_billing_day}</span>
                        </td>
                        <td className="px-2 py-1.5 text-[#73706A]">{(s.current_period_end || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-[#FFF3E6] border-l-4 border-[#E85D04] rounded-r-md p-3 text-xs text-[#1A1A1A]">
                <strong>Plano C:</strong> assinaturas <span className="text-[#2D6A4F] font-semibold">no dia 01</span> ficam intactas. As <span className="text-[#9B2226] font-semibold">desalinhadas</span> serao movidas para vencimento permanente no <strong>dia 05</strong>.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Fechar</Button>
            <Button
              disabled={realignLoading || !auditData || auditData.misaligned === 0}
              onClick={async () => {
                if (!window.confirm(`Confirmar realinhamento de ${auditData?.misaligned} assinatura(s) para o dia 05? As que ja estao no dia 01 serao mantidas.`)) return;
                setRealignLoading(true);
                try {
                  const { data } = await axios.post(`${API}/admin/subscriptions/realign-day-01`, { target_day: 5 }, { withCredentials: true });
                  alert(`Concluido!\nAtualizadas: ${data.updated}\nMantidas (ja alinhadas dia 01 ou 05): ${data.skipped_already_aligned}\nFalharam: ${data.failed}\nProximo anchor: ${data.anchor_iso}`);
                  // Refresh audit
                  setAuditData(null);
                  setAuditLoading(true);
                  const r = await axios.get(`${API}/admin/subscriptions/audit`, { withCredentials: true });
                  setAuditData(r.data);
                  setAuditLoading(false);
                } catch (e) {
                  alert('Erro: ' + (e.response?.data?.detail || e.message));
                } finally {
                  setRealignLoading(false);
                }
              }}
              className="bg-[#0077B6] hover:bg-[#03045E] text-white"
              data-testid="realign-day-05-btn"
            >
              {realignLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : null}
              Realinhar desalinhadas para dia 05
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
